import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchCampaignRequestTimeline,
  downloadCsv,
  CampaignRequestTimelineResponse,
  CampaignRequestTimelineRow,
} from '~/services/predefinedReports';
import { PredefinedResultsLayout, Column } from './PredefinedResultsLayout';

// Dates arrive as plain YYYY-MM-DD. Parsing at local midnight keeps them from
// sliding a day back in negative-offset timezones.
const formatDate = (value: string | null) =>
  value ? new Date(`${value}T00:00:00`).toLocaleDateString() : '—';

// A blank duration means the milestone hasn't happened yet, which is different
// from taking zero days — so it gets a dash, not a 0.
const formatDays = (value: number | null) => (value === null ? '—' : value);

const columns: Column<CampaignRequestTimelineRow>[] = [
  { key: 'ntaf_id', label: 'NTAF ID' },
  { key: 'client_name', label: 'Client' },
  { key: 'csc_name', label: 'CSC' },
  { key: 'request_created', label: 'Request Created', render: (r) => formatDate(r.request_created) },
  { key: 'request_approved', label: 'Approved', render: (r) => formatDate(r.request_approved) },
  {
    key: 'application_completed',
    label: 'Application Completed',
    render: (r) => formatDate(r.application_completed),
  },
  { key: 'campaign_open', label: 'Campaign Open', render: (r) => formatDate(r.campaign_open) },
  {
    key: 'days_to_approval',
    label: 'Days to Approval',
    rightAlign: true,
    render: (r) => formatDays(r.days_to_approval),
  },
  {
    key: 'days_approval_to_application',
    label: 'Approval → Application',
    rightAlign: true,
    render: (r) => formatDays(r.days_approval_to_application),
  },
  {
    key: 'days_application_to_open',
    label: 'Application → Open',
    rightAlign: true,
    render: (r) => formatDays(r.days_application_to_open),
  },
  {
    key: 'days_total',
    label: 'Total Days',
    rightAlign: true,
    render: (r) => formatDays(r.days_total),
  },
];

const CampaignRequestTimelineResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<CampaignRequestTimelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const params = {
      from_date: searchParams.get('from_date') || undefined,
      to_date: searchParams.get('to_date') || undefined,
      csc: searchParams.get('csc') || undefined,
    };
    setLoading(true);
    fetchCampaignRequestTimeline(params)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => { setError(e.message); setData(null); })
      .finally(() => setLoading(false));
  }, [searchParams]);

  const range =
    data?.from_date && data?.to_date
      ? `Requests created ${formatDate(data.from_date)} – ${formatDate(data.to_date)}`
      : 'Campaign requests that reached a campaign';

  return (
    <PredefinedResultsLayout
      title='Campaign Request to Campaign Open'
      subtitle={range}
      columns={columns}
      rows={data?.rows ?? []}
      loading={loading}
      error={error}
      rowKey={(r) => r.campaign_request_id}
      onDownloadCsv={() => downloadCsv('campaign-request-timeline', searchParams)}
    />
  );
};

export default CampaignRequestTimelineResults;
